/* eslint-disable @typescript-eslint/no-explicit-any */

import { authActions, authTypes } from '@store/slices/auth';
import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getServices } from './service';
import { baseClient, getAuthCookie } from '../index';

function openLoginWindow(store: any) {
  const loginUrl = window.location.origin.includes('dev')
    ? 'https://portaldev.telnyx.com/#/login/sign-in?close_login_on_success=true'
    : 'https://portal.telnyx.com/#/login/sign-in?close_login_on_success=true';

  const width = 700;
  const height = 800;

  const left = window.screen.width / 1.325 - width / 1.325;
  const top = window.screen.height / 2 - height / 2;

  const features = `width=${width},height=${height},left=${left},top=${top}`;
  const loginWindow = window.open(loginUrl, 'LoginWindow', features);

  const timer = setInterval(() => {
    if (loginWindow?.closed) {
      clearInterval(timer);

      store.dispatch(authActions.setLoginLoading(false));
      store.dispatch(login({ reattempt: true }));
    }
  }, 1000);
}

export function deleteCookie(name: string, path: string, domain: string) {
  if (getCookie(name)) {
    document.cookie =
      name +
      '=' +
      (path ? ';path=' + path : '') +
      (domain ? ';domain=' + domain : '') +
      ';expires=Thu, 01 Jan 1970 00:00:01 GMT' +
      ';secure;SameSite=None'; // Include if the cookie has these attributes
  }
}

function getCookie(name: string) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

export const login = createAsyncThunk(
  'auth/login',
  async (payload: { reattempt?: boolean } | null = null, store: any) => {
    const userAuthToken = getAuthCookie();

    if (!userAuthToken && payload?.reattempt) {
      const error = 'Authentication failed. You did not complete the portal login process in the new window.';
      return { error };
    }

    if (!userAuthToken) {
      openLoginWindow(store);
      return {
        portalLogin: true,
      };
    }

    try {
      // const response = await baseClient.post('/login');
      const response = await baseClient.post(LOCAL || INTERNAL_CLIENT ? '/auth/login' : '/login');

      if (!response?.data) return { error: 'The client failed to login. Please try again.' };

      store.dispatch(getServices());

      return response.data;
    } catch (e) {
      console.error('Login error', e);

      const error =
        'There was an issue authenticating your login token. If you are already logged in to the Telnyx Portal, you will need to sign out first. Then refresh Flow and try login again.';

      // fix for users not able to login, will delete the cookie and reprompt login
      deleteCookie('flow_auth', '/', '.telnyx.com');

      return { error };
    }
  }
);

export const refresh = createAsyncThunk('auth/refresh', async (_, store): Promise<any> => {
  const response = await baseClient.get(LOCAL || INTERNAL_CLIENT ? '/auth/status' : '/status');
  if (response?.data?.error) return { error: response.data.error };
  if (!response?.data) return { error: 'The client failed to login. Please try again.' };

  store.dispatch(getServices());
  return response.data;
});

export const loginThunk = (builder: ActionReducerMapBuilder<authTypes>) => {
  builder.addCase(login.pending, (state: authTypes, action: PayloadAction<any>) => {
    if (!action?.payload?.portalLogin) state.loginWindowOpen = false;
    state.error = null;
    state.loginLoading = true;
  });

  builder.addCase(login.fulfilled, (state: authTypes, action: PayloadAction<any>) => {
    const payload = action.payload;

    if (payload?.portalLogin) {
      state.loginLoading = true;
      state.loginWindowOpen = true;
      return;
    }

    state.loginLoading = false;
    state.refreshLoading = false;
    state.loginWindowOpen = false;

    if (state.logoutLoading) return;

    state.invalidPermission = payload?.invalidPermission || false;

    if (payload?.error) {
      if (!payload?.error?.includes('not authenticated')) {
        state.error = payload.error;
      }

      return;
    }

    if (payload?.name) state.name = payload.name;
    if (payload?.username) state.username = payload.username;
    if (payload?.email) state.email = payload.email;

    if (payload?.security_passphrase) state.security_passphrase = payload.security_passphrase;
    if (payload?.role) state.role = payload.role;
    if (payload?.country) state.country = payload.country;
    if (payload?.phone_number) state.phone_number = payload.phone_number;
    if (payload?.user_id) state.user_id = payload.user_id;
    if (payload?.user_hash) state.user_hash = payload.user_hash;
    if (payload?.organization_id) state.org_id = payload.user_id;

    state.isTelnyxUser = payload.email.endsWith('@telnyx.com') || false;
    state.telnyxFeatures =
      (payload.email.endsWith('@telnyx.com') && !localStorage.getItem('disableTelnyxFeatures')) || false;

    state.error = null;
    state.isLoggedIn = true;
  });
  builder.addCase(login.rejected, (state: authTypes) => {
    state.loginLoading = false;
    state.refreshLoading = false;
    state.loginWindowOpen = false;
    state.error = 'The client failed to connect to the backend to validate your authentication status.';
  });
  builder.addCase(refresh.pending, (state: authTypes) => {
    state.error = null;
    state.refreshLoading = true;
    state.loginLoading = true;
  });
  builder.addCase(refresh.fulfilled, (state: authTypes, action: PayloadAction<any>) => {
    const payload = action.payload;

    state.loginLoading = false;
    state.refreshLoading = false;

    state.invalidPermission = payload?.invalidPermission || false;

    if (payload?.error) return;

    if (payload?.name) state.name = payload.name;
    if (payload?.username) state.username = payload.username;
    if (payload?.email) state.email = payload.email;
    if (payload?.security_passphrase) state.security_passphrase = payload.security_passphrase;
    if (payload?.role) state.role = payload.role;
    if (payload?.country) state.country = payload.country;
    if (payload?.phone_number) state.phone_number = payload.phone_number;

    if (payload?.id) state.user_id = payload.id;
    if (payload?.user_hash) state.user_hash = payload.user_hash;
    if (payload?.organisation_id) state.org_id = payload.organisation_id;

    state.isTelnyxUser = payload?.email?.endsWith('@telnyx.com') || false;
    state.telnyxFeatures =
      (payload.email.endsWith('@telnyx.com') && !localStorage.getItem('disableTelnyxFeatures')) || false;

    state.error = null;
    state.isLoggedIn = true;
    state.refreshLoading = false;
  });

  builder.addCase(refresh.rejected, (state) => {
    state.refreshLoading = false;
    state.loginLoading = false;
    state.error = null;
  });
};
